/* eslint-disable max-lines */
import type { EcsProjectTeamName } from '../types';

// Register ECS Experiments and Rollouts in this file, both in the following enum and in the configuration data below.
//
// ECS Experiments and Rollouts are handled the same here; the main difference is that Rollouts do not have a scorecard
// generated for them and don't need to be reported.
//
// This also currently contains legacy CT experiments, which should be migrated to ECS experiments before the cut-off
// date in March 2026.

export type Experiment =
  | 'AdhocAudienceAnnouncementV2'
  | 'AdvancedModerationTableWithSort'
  | 'AlwaysEnableJoinInEventButton'
  | 'AnalyticsInCCDashboard'
  | 'AnalyticsThreadPreview'
  | 'AnswersForager'
  | 'AnswersForagerGroups'
  | 'AnswersToggle'
  | 'AnswersToggleUpdate'
  | 'AnswersUpdates'
  | 'ArticleFullViewInModal'
  | 'ArticleInMTOCommunity'
  | 'ArticleModalDialog'
  | 'AttachmentLimit'
  | 'AttachmentsInCommunityEventsPublisher'
  | 'AuthGatedLinkPreviewSuppression'
  | 'AutoEnrolledCommunityCreation'
  | 'BackendCellOverride'
  | 'BackendGraphqlApiV5Endpoint'
  | 'BespokeCommunityCreateEdit'
  | 'CKReplyEditPublisher'
  | 'CKReplyPublisher'
  | 'CKThreadStarterEditPublisher'
  | 'CKThreadStarterPublisher'
  | 'CommsProjectAnalyticsPerOwnerMetrics'
  | 'CommunityAgentAdmin'
  | 'CommunityAgentAnalytics'
  | 'CommunityAgentAtMention'
  | 'CommunityAgentCoachingBar'
  | 'CommunityAgentCustomInstructions'
  | 'CommunityAgentDefaultSPGroundingOptOut'
  | 'CommunityAgentDogfood'
  | 'CommunityAgentinTeams'
  | 'CommunityAgentProposalSharing'
  | 'CommunityAgentPreview'
  | 'CommunityAgentPrivatePreview'
  | 'CommunityAgentPublicEngageContent'
  | 'CommunityAgentSocialUXForSources'
  | 'CommunityAgentSortProposals'
  | 'CommunityAgentSPGrounding'
  | 'CommunityAgentWebGrounding'
  | 'CommunityAgentSkippedDismissedFeed'
  | 'CommunityAIGeneratedBanner'
  | 'CommunityBadgeAdmin'
  | 'CommunityDynamicGroupMembership'
  | 'CommunityFeedNavAndSort'
  | 'CommunityLeads'
  | 'CommunityLeadsAndAutoPin'
  | 'CommunityRegularAnnouncementNotificationTracker'
  | 'CommunityRelayBanner'
  | 'CommunityRelayDataLoader'
  | 'CompactHeaderGeneral'
  | 'CompactHeaderStoryline'
  | 'CondensedQuestionFeed'
  | 'ConversationAnalyticsDwellTime'
  | 'ConversationClickMetrics'
  | 'CopilotCommunitySuggestedContentBannerNotificationWebv3'
  | 'CopilotCommunitySuggestedContentBellNotificationWeb'
  | 'CopyCodeSnippet'
  | 'CorpCommsProjects'
  | 'CorpCommsProjectsAnalyticsDashboard'
  | 'CorpCommsProjectsAnalyticsV2'
  | 'CorpCommsProjectsUnstable'
  | 'CreateCommunityInTeamsTBE'
  | 'CustomAndProactiveAnalyticsReports'
  | 'CustomInstructionsResponsePreview'
  | 'DashboardHeaderM365ConferenceBanner'
  | 'DeprecateLegacyEvent'
  | 'DisableCopilotHistory'
  | 'DefaultHomeFeedFilter'
  | 'DisableLegacyEventCreation'
  | 'DisableHomePremiumEventsCard'
  | 'DisablePrivatePremiumEventCreation'
  | 'DisablePollPostType'
  | 'DivisionalBespokeCommunity'
  | 'DogfoodPerfHUD'
  | 'EngageCustomBrandingTheming'
  | 'EnableCalendarInvitesForAttendees'
  | 'EnableDeleteOptions'
  | 'EnableGuestAccessToPremiumEvents'
  | 'EngageEventFacilitator'
  | 'EngageIASimplificationNavRefresh'
  | 'EngageIASimplificationPhase1'
  | 'EngagePairwiseBrokerHosting'
  | 'EngageWebMemberTabForAutoEnrollCmty'
  | 'EventPolicyCheck'
  | 'EventsMonetizationCapacity'
  | 'EventPreviews'
  | 'EventsAttendeeViewMode'
  | 'EventsECDNSupportForNetworkAccessPolicy'
  | 'ExpertNominationToggle'
  | 'ExportNetworkLeaderboard'
  | 'ExtendedUnSeenCountEnabled'
  | 'EventCoorganizerEdits'
  | 'EventsCustomCoverImage'
  | 'FeatureConversationsAudience'
  | 'FeedLoadNumThreads'
  | 'FeedLoadMoreNumThreads'
  | 'FilesCookielessAuth'
  | 'FilesColdStorage'
  | 'FileUploadInTeamsQNA'
  | 'FollowButtonHomeFeedThreadWithGreyColor'
  | 'GifPickerMode'
  | 'GroupFeedFilterAndSortByExp'
  | 'GroupFeedSortRecentPostsWeb'
  | 'GroupHoverCardV2'
  | 'GroupMembershipHealthIssues'
  | 'GuidedAdminNotificationsLandingPage'
  | 'HideFilesRelatedResources'
  | 'HomeFollowingFeed'
  | 'IFrameSandboxForSharePointVideoCard'
  | 'InlineSharedMessagePreview'
  | 'ImageCollageV2'
  | 'ImmersiveMediaViewCarousel'
  | 'IncrementalDelivery'
  | 'InboxReplatform'
  | 'InFeedUserSuggestions'
  | 'InformationBarriersPhase0'
  | 'IntelligentImporterBulkDelete'
  | 'IntelligentImporterDeleteOnPublish'
  | 'KnowledgeBespokeCommunity'
  | 'LeaderAudienceFeed'
  | 'LeadersCascadeCTA'
  | 'LeftNavPagination'
  | 'LightboxFollowUsers'
  | 'LinkPreviewForSuggestedContent'
  | 'LoadThreadDataWhileRouting'
  | 'LoadFeedDataWhileRouting'
  | 'LogProtobufEvents'
  | 'LoopFilePreview'
  | 'M365CopilotAdoptionTBE'
  | 'MarkdownCopyPaste'
  | 'MarkdownFilePreview'
  | 'MIPAssignedLabelReadOnlyFallback'
  | 'MIPEnabledForEveryone'
  | 'MessagesReactionsAPIV2'
  | 'ModerationAgent'
  | 'ModerationAgentBlockPost'
  | 'MoreForYouStoryline'
  | 'MTOBlendedInbox'
  | 'MTOPolicyEngageAdminPortalPage'
  | 'MTOSpokeMemberAdmin'
  | 'EnablePermalinkDeeplink'
  | `MultiTenantOrganization`
  | 'NaaAppIdEngageOutlookMetaOs'
  | 'NaaAppIdEngageTeamsMetaOs'
  | 'NaaNewAppIdsOnboarding'
  | 'MyTeamAudience'
  | 'NetworkCommunityAgentAnalytics'
  | 'NetworkInsightsLazyCards'
  | 'NetworkInsightsV2'
  | 'NetworkQuestionsInConversations'
  | 'NotificationProgressTracker'
  | 'NotificationProgressTrackerForCommunity'
  | 'NovaEventAnalytics'
  | 'OnboardingForCommunitiesInTeams'
  | 'OnePlayerReliabilityV1'
  | 'OnePlayerV2'
  | 'OpenPublisherWithModal'
  | 'OrgDataGovernance'
  | 'OrgDataQuality'
  | 'PeopleSuggestion3SV3'
  | 'PinnedFiles'
  | 'PrefilledDefaultAudience'
  | 'PremiumEventUpdates'
  | 'PromptFirstThreads'
  | 'PublisherAttachmentPermissionCheck'
  | 'PublisherPreviews'
  | 'PublisherVersion2'
  | 'PublisherWarningDialog'
  | 'PWALegacyBrokenExperience'
  | 'QnAOneEventsInfiniteScroll'
  | 'QnaScrollChaining'
  | 'QrCodeFeature'
  | 'QuestionsQueue'
  | 'LeadersSettingCard'
  | 'ReactionSimplification'
  | 'RealtimeCometdDebugLogs'
  | 'RealtimeDebugEventReporting'
  | 'RichAttachmentPreviewsEnabled'
  | 'RichVideoPreviewsEnabled'
  | 'RoleBasedFRE'
  | 'SaveAdhocAudience'
  | 'SearchDiscoverabilityFRE'
  | 'SearchAutocompleteTrendingThemeAndHistory'
  | 'SearchInThisStoryline'
  | 'SeededLeader'
  | 'SimplifiedFRE'
  | 'SharePointPacTokenByTenant'
  | 'SharePointTempAuthUrlDeprecationByTenant'
  | 'SharedDrafts'
  | 'SmartInbox'
  | 'SocialBarV2'
  | 'SPNewsIntegration'
  | 'SpNewsMessageType'
  | 'StorylineAnnouncementNotificationUrgency'
  | 'StorylineEmptyFeedEducationModule'
  | 'StorylineEmptyFeedTeamsExperience'
  | 'StorylineFeedFiltersTBE'
  | 'StorylineEventsSwimlaneView'
  | 'SuggestedArticleFormatForSeeded'
  | 'SuggestedContentBellNotification'
  | 'TBEBackgroundLoad'
  | 'TBEFeedLoadCacheTtl3Days'
  | 'TBEConnectionsSuggestion'
  | 'TBEMultiLanguagePublisher'
  | 'TBENewFollowersPage'
  | 'TBEShortcutRelay'
  | 'TBESuggestedContent'
  | 'TBESwStaticRouting'
  | 'TeamsBackButton'
  | 'TeamsBrandedExperiencesCachedFeedLoad'
  | 'TeamsBrandedExperiencesTimeoutBasedCachedFeedLoad'
  | 'TeamsBrandedExperiencesCommunityCDLSync'
  | 'TeamsBrandedExperiencesCommunityFeatures'
  | 'CommunityInTeamsReplatDF'
  | 'TeamsBrandedExperiencesIdbCache'
  | 'TeamsBrandedExperiencesEvents'
  | 'TeamsBrandedExperiencesLastReadBar'
  | 'TeamsBrandedExperiencesFilesTab'
  | 'TeamsQnAInOneEventsLandingPage'
  | 'TeamsQnaLocalDevelopment'
  | 'TeamsQnAPostAsModeratorSenderDetails'
  | 'TeamsQnARoutingHeadersForSplitRegion'
  | 'TeamsVirtualEventsServiceEnv'
  | 'ThreadDwellTime'
  | 'ToggleEventModeration'
  | 'TownhallEmbedRingOverride'
  | 'TownhallRecordingUrlThresholdMinutes'
  | 'UseEditV1Api'
  | 'UserThemesDeprecation'
  | 'PremiumEventExperience'
  | 'VivaEngageCampaignPlugin'
  | 'VivaEngageCopilotDebug'
  | 'VivaEngageCopilotAppChat'
  | 'VivaEngageCatalystSDK'
  | 'VivaEngageDeepLinkFromOWA'
  | 'VivaEngageAugLoopEnvironment'
  | 'UnseenThreadStarterInInbox'
  | 'EnableGranularPermissions'
  | 'GetActiveVideoReferences'
  | 'EngageAgent'
  | 'TakeActionAgent'

  // Existing experiments that incorrectly have 'Enabled' as part of their name.
  // Do not do this - 'enabled' should be |of an experiment not part of the name.
  // The name should instead focus on describing the feature/functionality affected.
  // Since these are |and in use we |t rename them so just have
  // to let them run their course.
  | 'ModulatorGroupSuggestionsEnabled'

  // Experiments used for testing the experiment infrastructure - grouping
  // these separately from the regular feature experiments above. Regular
  // experiments should not have the name 'experiment' in them.
  | 'ClientForwardExperimentForTesting1'
  | 'ClientForwardExperimentForTesting2'
  | 'CrossPlatformExperimentForTesting1'
  | 'CrossPlatformExperimentForTesting2'
  | 'ExperimentForTesting1'
  | 'ExperimentForTesting2';

export type ExperimentTreatmentValue = string | boolean | number;

export interface ExperimentConfig<T extends readonly ExperimentTreatmentValue[]> {
  readonly id: number | string | undefined; // Legacy CT experiments use string guid; new ECS experiments/rollouts use numeric id.
  readonly projectTeam: EcsProjectTeamName;
  readonly treatments: T;
  readonly defaultTreatment: T[number];
}

export const experimentConfigs = {
  AdhocAudienceAnnouncementV2: {
    id: 1773198,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AdvancedModerationTableWithSort: {
    id: 'aab97be9-046f-4666-80be-357a2bf70e4b',
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AlwaysEnableJoinInEventButton: {
    id: 1769896,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AnalyticsInCCDashboard: {
    id: '7c3370f5-ca44-4678-867c-c521007a8fdc',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AnalyticsThreadPreview: {
    id: 1872785,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AnswersForager: {
    id: 'e86fc706-4e92-4fa3-8376-199716e811ee',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  AnswersForagerGroups: {
    id: '18278c4f-67a3-4760-b90c-ba4e80253582',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  AnswersToggle: {
    id: '4235a1a4-f403-4426-b53d-889880fb51a4',
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  AnswersToggleUpdate: {
    id: 'bf9daeec-d0a3-483a-9714-d7402758f1bd',
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AnswersUpdates: {
    id: '4f9555aa-a589-487f-8b8b-a8a43fa3b9ab',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ArticleFullViewInModal: {
    id: 'b9ba67f8-ac90-436b-91f9-24d9ca1cbd80',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ArticleInMTOCommunity: {
    id: 1788421,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ArticleModalDialog: {
    id: 1764647,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AttachmentLimit: {
    id: 1856516,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AttachmentsInCommunityEventsPublisher: {
    id: 1765798,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AuthGatedLinkPreviewSuppression: {
    id: 1852087,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  AutoEnrolledCommunityCreation: {
    id: 1793336,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  BackendCellOverride: {
    id: 1770375,
    projectTeam: 'ClientForward',
    treatments: ['control', 'c1', 'c2', 'c3', 'c4', 'c5'],
    defaultTreatment: 'control',
  },
  LeadersSettingCard: {
    id: '60615d03-433d-468a-8753-c889e4777325',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  BackendGraphqlApiV5Endpoint: {
    id: '350d9e19-b73d-4c66-aef4-ff9729c625c4',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  BespokeCommunityCreateEdit: {
    id: '63d38f2f-36df-4d03-b8ed-fa4f7e07b48f',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CKReplyEditPublisher: {
    id: 1854748,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CKReplyPublisher: {
    id: 1781429,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CKThreadStarterEditPublisher: {
    id: 1860020,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CKThreadStarterPublisher: {
    id: 1783811,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommsProjectAnalyticsPerOwnerMetrics: {
    id: 1872324,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentAdmin: {
    id: 1763694,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  CommunityAgentAnalytics: {
    id: 1814354,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentAtMention: {
    id: 1755524,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  CommunityAgentCoachingBar: {
    id: 1855538,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentCustomInstructions: {
    id: 1811135,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentDefaultSPGroundingOptOut: {
    id: 1820135,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentDogfood: {
    id: 1759925,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentProposalSharing: {
    id: 1812165,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentinTeams: {
    id: 1753517,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentPreview: {
    id: 1819807,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  CommunityAgentPrivatePreview: {
    id: 1763717,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentPublicEngageContent: {
    id: 1809936,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentSocialUXForSources: {
    id: 1811154,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentSortProposals: {
    id: 1792606,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAgentSPGrounding: {
    id: 1760322,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  CommunityAgentWebGrounding: {
    id: 1829946,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  CommunityAgentSkippedDismissedFeed: {
    id: 1850323,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityAIGeneratedBanner: {
    id: '1da6aa2e-1c02-4524-882a-e81a7260ab6d',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityBadgeAdmin: {
    id: 'ace0c8c8-713f-433d-9807-2617506e7cbb',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityDynamicGroupMembership: {
    id: 1774830,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityFeedNavAndSort: {
    id: 1852119,
    projectTeam: 'YammerClients',
    treatments: [false, 'variant1_counts', 'variant2_bold'],
    defaultTreatment: false,
  },
  CommunityLeads: {
    id: '25f7fc54-ecbd-495e-9014-3bae205e8f91',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityLeadsAndAutoPin: {
    id: 'beae692c-0343-4008-8003-64d56a4b8f19',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityRegularAnnouncementNotificationTracker: {
    id: 1840779,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityRelayBanner: {
    id: 'db5c0e91-2fa0-47a7-81c5-81a451f2e0e9',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityRelayDataLoader: {
    id: '77c8c9d4-133c-4f72-a0e5-682f46dba497',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CompactHeaderGeneral: {
    id: '3a8152dc-3f83-4334-ad84-be9acbbdb504',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CompactHeaderStoryline: {
    id: 'affd7fb3-e3d6-4e99-968e-786e19ff8295',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CondensedQuestionFeed: {
    id: '5bcf59f7-1b56-4445-8af4-6c373d676591',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ConversationAnalyticsDwellTime: {
    id: 1849793,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ConversationClickMetrics: {
    id: 1850275,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CopilotCommunitySuggestedContentBannerNotificationWebv3: {
    id: 1824313,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CopilotCommunitySuggestedContentBellNotificationWeb: {
    id: 1791032,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CopyCodeSnippet: {
    id: 1855829,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CorpCommsProjects: {
    id: 1808195,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CorpCommsProjectsAnalyticsDashboard: {
    id: 1823130,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CorpCommsProjectsAnalyticsV2: {
    id: 1872466,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CorpCommsProjectsUnstable: {
    id: 1853593,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ClientForwardExperimentForTesting1: {
    id: undefined,
    projectTeam: 'ClientForward',
    treatments: ['control', 'c1', true],
    defaultTreatment: 'control',
  },
  ClientForwardExperimentForTesting2: {
    id: undefined,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CrossPlatformExperimentForTesting1: {
    id: undefined,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'c1'],
    defaultTreatment: 'control',
  },
  CrossPlatformExperimentForTesting2: {
    id: undefined,
    projectTeam: 'CrossPlatform',
    treatments: [false, true, 'c2', 'control'],
    defaultTreatment: false,
  },
  CreateCommunityInTeamsTBE: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CustomInstructionsResponsePreview: {
    id: 1853768,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CustomAndProactiveAnalyticsReports: {
    id: 1861261,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DeprecateLegacyEvent: {
    id: 1755607,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DashboardHeaderM365ConferenceBanner: {
    id: 1766436,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DisableCopilotHistory: {
    id: 1761272,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DisableHomePremiumEventsCard: {
    id: 1769915,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DisableLegacyEventCreation: {
    id: 1761121,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DisablePrivatePremiumEventCreation: {
    id: 1770429,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DisablePollPostType: {
    id: '57738844-f4ad-4e10-bc25-8b1736ceae70',
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DivisionalBespokeCommunity: {
    id: '6f9c3ced-854b-4bc2-9af3-7db4be804007',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DogfoodPerfHUD: {
    id: 1789139,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EnableCalendarInvitesForAttendees: {
    id: 1840569,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  DefaultHomeFeedFilter: {
    id: 1862691,
    projectTeam: 'CrossPlatform',
    treatments: [false, 'discovery', 'chronological'],
    defaultTreatment: false,
  },
  EnableDeleteOptions: {
    id: 'd3c72095-a654-4ba6-bf11-380b9cd63c3d',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EngageCustomBrandingTheming: {
    id: 1764480,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EngageEventFacilitator: {
    id: 1792806,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EngageIASimplificationNavRefresh: {
    id: 1872944,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EngageIASimplificationPhase1: {
    id: 1851385,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EngagePairwiseBrokerHosting: {
    id: 1820984,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: true,
  },
  EngageWebMemberTabForAutoEnrollCmty: {
    id: 1792779,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },

  EventPreviews: {
    id: 'ccd189e8-0d5a-41cc-b712-6700e43b913f',
    projectTeam: 'ClientForward',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  EventCoorganizerEdits: {
    id: 1777452,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EventsAttendeeViewMode: {
    id: 1799112,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EventsCustomCoverImage: {
    id: 1794595,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EventsECDNSupportForNetworkAccessPolicy: {
    id: 1776544,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ExperimentForTesting1: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled', 'c2', 't1', false, 'tservice', 'toverride', 'user-context-t1'],
    defaultTreatment: 'control',
  },
  ExperimentForTesting2: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: [false, true, 5, 'control'],
    defaultTreatment: false,
  },
  ExpertNominationToggle: {
    id: '3c00bea5-5810-4dd2-b6cf-37c1e5de2b66',
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  ExportNetworkLeaderboard: {
    id: 1777529,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ExtendedUnSeenCountEnabled: {
    id: 1766530,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  FeatureConversationsAudience: {
    id: 1781377,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  FeedLoadNumThreads: {
    id: 1787497,
    projectTeam: 'YammerClients',
    treatments: [4, 3, 2, 1],
    defaultTreatment: 4,
  },
  FeedLoadMoreNumThreads: {
    id: 1787508,
    projectTeam: 'YammerClients',
    treatments: [4, 3, 2, 1],
    defaultTreatment: 4,
  },
  FilesColdStorage: {
    id: 1771038,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  FilesCookielessAuth: {
    id: '63a5ce7d-ecb2-4090-a988-77b39994f4d7',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  FileUploadInTeamsQNA: {
    id: 1819892,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  FollowButtonHomeFeedThreadWithGreyColor: {
    id: '440bb634-1b43-44c8-9251-e9d02a469570',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  GifPickerMode: {
    id: 1857039,
    projectTeam: 'ClientForward',
    treatments: ['control', 'disabled', 'tenor', 'giphy'],
    defaultTreatment: 'control',
  },
  GroupFeedFilterAndSortByExp: {
    id: 'cff752e6-7035-4cd5-9264-d6340d64b3d9',
    projectTeam: 'YammerClients',
    treatments: ['all_recent_posts', 'default'],
    defaultTreatment: 'default',
  },
  GroupFeedSortRecentPostsWeb: {
    id: 1784634,
    projectTeam: 'ClientForward',
    treatments: ['enabled_with_extras', true, false],
    defaultTreatment: false,
  },
  GuidedAdminNotificationsLandingPage: {
    id: 1785897,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  HideFilesRelatedResources: {
    id: 1770452,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  HomeFollowingFeed: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  IFrameSandboxForSharePointVideoCard: {
    id: '76d544b1-f002-49ff-82dd-b9aeb6344f0d',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  InlineSharedMessagePreview: {
    id: 1808807,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ImageCollageV2: {
    id: 1786489,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  ImmersiveMediaViewCarousel: {
    id: 1794333,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  IncrementalDelivery: {
    id: 1841062,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  InFeedUserSuggestions: {
    id: 'edc8645c-a10d-4ab1-aab3-34842a682c5d',
    projectTeam: 'ClientForward',
    treatments: ['control', 'rankie', 'enabled'],
    defaultTreatment: 'control',
  },
  GroupHoverCardV2: {
    id: 1774731,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  GroupMembershipHealthIssues: {
    id: 1840903,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  InboxReplatform: {
    id: 1754635,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  InformationBarriersPhase0: {
    id: 'f2e9d7b0-40ff-4d95-a80a-32ba65ab6a19',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: true,
  },
  IntelligentImporterBulkDelete: {
    id: '7604b46b-048c-4797-ab0e-5f2b60961dbf',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  IntelligentImporterDeleteOnPublish: {
    id: 'fc579934-8971-4492-b9fc-6badebc9f093',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  KnowledgeBespokeCommunity: {
    id: '992716ec-e54a-48cd-9885-996aaacc3cd3',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  LeaderAudienceFeed: {
    id: 1787459,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  LeadersCascadeCTA: {
    id: '27cadbee-91e2-4dcd-b8f3-7e2a6b735c59',
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  LeftNavPagination: {
    id: 1853483,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  LightboxFollowUsers: {
    id: 'd34c34e0-815c-4133-a17d-fe181895cf4d',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  LinkPreviewForSuggestedContent: {
    id: 1755083,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  LoadFeedDataWhileRouting: {
    id: '9b715056-ccf7-49f7-8cc8-a02ad138ce80',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  LoadThreadDataWhileRouting: {
    id: 1750432,
    projectTeam: 'ClientForward',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  LogProtobufEvents: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  LoopFilePreview: {
    id: 1751897,
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  M365CopilotAdoptionTBE: {
    id: 1776961,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MarkdownCopyPaste: {
    id: 1861254,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MarkdownFilePreview: {
    id: 1831834,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  MIPAssignedLabelReadOnlyFallback: {
    id: 1831054,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MIPEnabledForEveryone: {
    id: 1788471,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ModulatorGroupSuggestionsEnabled: {
    id: 'bfd3fd32-6e3c-41c0-a792-8962dab5d283',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ModerationAgent: {
    id: 1763833,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ModerationAgentBlockPost: {
    id: 1761956,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MoreForYouStoryline: {
    id: 1785828,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MTOBlendedInbox: {
    id: '50746bf4-b609-43de-9287-c67c52b25d7d',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MTOPolicyEngageAdminPortalPage: {
    id: 1778227,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MTOSpokeMemberAdmin: {
    id: 1774855,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EnablePermalinkDeeplink: {
    id: '76203ecf-f92b-43ef-8a76-5aad974ac86b',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MultiTenantOrganization: {
    id: 1764528,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NaaAppIdEngageOutlookMetaOs: {
    id: 1846907,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NaaAppIdEngageTeamsMetaOs: {
    id: 1846901,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NaaNewAppIdsOnboarding: {
    id: 1786471,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MyTeamAudience: {
    id: 1754648,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  NetworkCommunityAgentAnalytics: {
    id: 1831974,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NetworkInsightsLazyCards: {
    id: 1850197,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NetworkInsightsV2: {
    id: 1849058,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NetworkQuestionsInConversations: {
    id: 'b844c74d-8d80-41f4-968d-ca676ec73a76',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  OnboardingForCommunitiesInTeams: {
    id: 1774959,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  OnePlayerReliabilityV1: {
    id: 1779122,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  OnePlayerV2: {
    id: 1773105,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  OpenPublisherWithModal: {
    id: '09f483a5-d75b-4ecc-840a-604411989c70',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  OrgDataQuality: {
    id: 1787482,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  OrgDataGovernance: {
    id: 1788416,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PeopleSuggestion3SV3: {
    id: '5d873e2e-b60c-43d5-801a-716cce0894bc',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  PinnedFiles: {
    id: 'b2b7ae31-803f-42f8-833b-5d99489cc3a5',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PrefilledDefaultAudience: {
    id: 'a7c7ce2f-c586-40c7-abae-8d2c916a3572',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PremiumEventUpdates: {
    id: 'fb41ba99-36a2-432e-ae4a-0eb5c5fe9138',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PromptFirstThreads: {
    id: 1780401,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PublisherAttachmentPermissionCheck: {
    id: 1825738,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PublisherPreviews: {
    id: 1812381,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  QnAOneEventsInfiniteScroll: {
    id: 1787332,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  QnaScrollChaining: {
    id: 1791320,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  QuestionsQueue: {
    id: '2c93f569-5660-4f0d-8f86-d308cba0cf9f',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PublisherVersion2: {
    id: 1762490,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PublisherWarningDialog: {
    id: 1829803,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  QrCodeFeature: {
    id: 1741289,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PWALegacyBrokenExperience: {
    id: 'ede9763e-cfe8-4d4b-806f-21dd96be8409',
    projectTeam: 'YammerClients',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  ReactionSimplification: {
    id: '7119895f-2b5b-4e09-93a4-a745c0c96c48',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  RealtimeDebugEventReporting: {
    id: 'bf72c345-4045-4e0d-9d87-d0eb21ac5309',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  RealtimeCometdDebugLogs: {
    id: '74ef23b7-615c-4816-a7a0-735517efed16',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  RichAttachmentPreviewsEnabled: {
    id: 1824564,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  RichVideoPreviewsEnabled: {
    id: 1824567,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  RoleBasedFRE: {
    id: 1749356,
    projectTeam: 'CrossPlatform',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },

  SaveAdhocAudience: {
    id: 1779985,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },

  SearchDiscoverabilityFRE: {
    id: '179aa02b-969b-4288-b812-d2998c9b74e1',
    projectTeam: 'YammerClients',
    treatments: ['control', 'treatmenta', 'treatmentb'],
    defaultTreatment: 'control',
  },
  SearchAutocompleteTrendingThemeAndHistory: {
    id: '426e6aad-e967-409b-a2c2-acecb7bee41d',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SearchInThisStoryline: {
    id: 1854846,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SeededLeader: {
    id: 'bbbff046-d8f5-4177-ae8b-94360f942615',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SimplifiedFRE: {
    id: 1768524,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SharePointPacTokenByTenant: {
    id: 1818823,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SharePointTempAuthUrlDeprecationByTenant: {
    id: 1770487,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SharedDrafts: {
    id: 1812272,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SmartInbox: {
    id: 1855941,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SocialBarV2: {
    id: 1849310,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SPNewsIntegration: {
    id: 1799591,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SpNewsMessageType: {
    id: 1768529,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  StorylineAnnouncementNotificationUrgency: {
    id: '4915afb3-27fd-4dac-8a02-dd6cc20310c7',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  StorylineEmptyFeedTeamsExperience: {
    id: 1761027,
    projectTeam: 'ClientForward',
    treatments: ['control', 'enabled'],
    defaultTreatment: 'control',
  },
  StorylineEmptyFeedEducationModule: {
    id: 1759346,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  StorylineFeedFiltersTBE: {
    id: 1759337,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  StorylineEventsSwimlaneView: {
    id: 1760995,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SuggestedArticleFormatForSeeded: {
    id: 1731657,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  SuggestedContentBellNotification: {
    id: 1778621,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBEBackgroundLoad: {
    id: 1807815,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBEFeedLoadCacheTtl3Days: {
    id: 1776767,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBEConnectionsSuggestion: {
    id: 1777751,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBEMultiLanguagePublisher: {
    id: 'fc467fb9-c6de-4dbe-a436-18828bd75068',
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBENewFollowersPage: {
    id: 1777752,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBEShortcutRelay: {
    id: 1800107,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBESuggestedContent: {
    id: 1776963,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TBESwStaticRouting: {
    id: 1839959,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBackButton: {
    id: '78187586-38f7-4984-b4e4-b8b9ea9a06d5',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesCommunityCDLSync: {
    id: 1774683,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesCachedFeedLoad: {
    id: 1779989,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesTimeoutBasedCachedFeedLoad: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesCommunityFeatures: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  CommunityInTeamsReplatDF: {
    id: 1755765,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesIdbCache: {
    id: 1781245,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesLastReadBar: {
    id: 'c2c26c2f-8454-45a9-96b1-3961765ad7c7',
    projectTeam: 'YammerClients',
    treatments: [false, true, 'diagnostics_last_read_bar'],
    defaultTreatment: false,
  },
  TeamsBrandedExperiencesEvents: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: ['off', 'on', 'teams_storyline_only'],
    defaultTreatment: 'off',
  },
  TeamsBrandedExperiencesFilesTab: {
    id: '13c02fd2-c546-4d75-9020-31fc16fabd40',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsQnAInOneEventsLandingPage: {
    id: 1769903,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsQnaLocalDevelopment: {
    id: undefined,
    projectTeam: 'YammerClients',
    treatments: ['off', 'on'],
    defaultTreatment: 'off',
  },
  TeamsQnAPostAsModeratorSenderDetails: {
    id: '24204a26-d022-4e4e-a732-4be74b1e5a67',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsQnARoutingHeadersForSplitRegion: {
    id: 1761631,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TeamsVirtualEventsServiceEnv: {
    id: 1858392,
    projectTeam: 'ClientForward',
    treatments: ['DF', 'MSIT', 'PROD'],
    defaultTreatment: 'PROD',
  },
  ThreadDwellTime: {
    id: 1843752,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  ToggleEventModeration: {
    id: 1769902,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TownhallEmbedRingOverride: {
    id: '31f3462e-6d97-48e5-b275-1ee2940f506b',
    projectTeam: 'YammerClients',
    treatments: ['control', 'ring2', 'ring3', 'ring3_6', 'ring3_9', 'general'],
    defaultTreatment: 'control',
  },
  TownhallRecordingUrlThresholdMinutes: {
    id: 1779338,
    projectTeam: 'YammerClients',
    treatments: [60, 30, 90],
    defaultTreatment: 60,
  },
  UseEditV1Api: {
    id: '51ffdb63-5676-4db2-906f-8eb14ed1fba3',
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  UserThemesDeprecation: {
    id: 1751911,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  PremiumEventExperience: {
    id: 1769911,
    projectTeam: 'CrossPlatform',
    treatments: ['none', 'all'],
    defaultTreatment: 'none',
  },
  VivaEngageDeepLinkFromOWA: {
    id: '2809a765-1843-4189-9460-cb296610a0f7',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  VivaEngageAugLoopEnvironment: {
    id: 'f11bbd96-d6c3-46ab-866c-1662d9bfed90',
    projectTeam: 'YammerClients',
    treatments: ['int', 'dogfood', 'prod', 'test', 'msit'],
    defaultTreatment: 'prod',
  },
  VivaEngageCopilotDebug: {
    id: 1751156,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  VivaEngageCopilotAppChat: {
    id: 1752741,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  VivaEngageCatalystSDK: {
    id: 1780101,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  VivaEngageCampaignPlugin: {
    id: 'de15fe0d-5855-425d-9a34-0400ef1aece4',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  MessagesReactionsAPIV2: {
    id: 1755353,
    projectTeam: 'ClientForward',
    treatments: [false, true],
    defaultTreatment: false,
  },
  UnseenThreadStarterInInbox: {
    id: '6efec7a9-947a-4e04-a697-4d71dbd7c7af',
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NotificationProgressTracker: {
    id: 1761858,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NotificationProgressTrackerForCommunity: {
    id: 1815385,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  NovaEventAnalytics: {
    id: 1800692,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EnableGranularPermissions: {
    id: 1744299,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EnableGuestAccessToPremiumEvents: {
    id: 1779456,
    projectTeam: 'CrossPlatform',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EventsMonetizationCapacity: {
    id: 1813473,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EventPolicyCheck: {
    id: 1856827,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  GetActiveVideoReferences: {
    id: 1799694,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  EngageAgent: {
    id: 1805420,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
  TakeActionAgent: {
    id: 1818908,
    projectTeam: 'YammerClients',
    treatments: [false, true],
    defaultTreatment: false,
  },
} as const;

type ExperimentConfigsDefaultTreatments = {
  readonly [E in Experiment]: {
    readonly defaultTreatment: (typeof experimentConfigs)[E]['treatments'][number];
  };
};

export type ExperimentConfigs = typeof experimentConfigs;

experimentConfigs satisfies Record<Experiment, ExperimentConfig<readonly ExperimentTreatmentValue[]>>;
experimentConfigs satisfies ExperimentConfigsDefaultTreatments;

export type ExperimentTreatmentType<E extends Experiment> = (typeof experimentConfigs)[E]['treatments'][number];

export type ExperimentTreatments = {
  readonly [E in Experiment]-?: ExperimentTreatmentType<E>;
};

export const defaultExperimentTreatments: ExperimentTreatments = (
  Object.keys(experimentConfigs) as Experiment[]
).reduce(
  (acc: ExperimentTreatments, key: Experiment) => ({
    ...acc,
    [key]: experimentConfigs[key].defaultTreatment,
  }),
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  {} as ExperimentTreatments
);

export interface EcsExperiments {
  readonly treatments: ExperimentTreatments;
  readonly treatmentsFromService: Partial<ExperimentTreatments>;
  readonly treatmentOverrides: Partial<ExperimentTreatments>;
}
